/**
 * Clearing Department Action card items by hand, and Billing's cancellation cards (Traci, 25 Sep 2026). Pure.
 *
 * Manual clear ("X"): "I don't want cards to be impossible to clear just because the system is expecting a specific
 * action. Most action cards should have an X / manual clear option." Every card row carries an item_key; a clear is
 * stored as (card, item_key) and hides that item from that card only. Flag cards are not cleared here: a Flag is
 * resolved in the Notification Center, which keeps its own record. The department that owns a card, and Admin, may
 * clear its items.
 *
 * Billing cards:
 *   24-Hour Cancellation — Refund: cancelled within 24 hours of being booked, with money paid and no refund recorded.
 *     "These generally require a refund." Clears itself when a refund is recorded, or Billing clears it by hand.
 *   Client Cancellations: every other cancellation, for Billing to review. Cleared by hand.
 * Both count cancellations from CANCELLATION_CARDS_SINCE, so the cards start empty-ish instead of listing years of
 * past cancellations.
 */

export const CANCELLATION_CARDS_SINCE = '2026-09-01'
export const REFUND_WINDOW_HOURS = 24
export const NOT_CLEARABLE = new Set(['admin_flags', 'sales_flags', 'billing_flags', 'talent_flags', 'editing_flags', 'applications_pending'])

/**
 * Sales' "New Bookings — Enter in GHL" (prototype v108 §2.1): every new booking until Sales marks it entered in GHL.
 * "Mark Entered in GHL" is stored like a clear, with the reason "Entered in GHL", and logged as entered_in_ghl.
 * Counts bookings made from GHL_SINCE so the card starts with this week's bookings, not every booking ever.
 */
export const GHL_SINCE = '2026-09-20'
export const GHL_REASON = 'Entered in GHL'

const denverWhen = iso => {
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? d.toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
}

/** New bookings for GHL entry: real, not cancelled, made on or after GHL_SINCE. contacts: customer id -> { email, phone }. */
export function ghlEntryRows({ bookings = [], names = new Map(), contacts = new Map(), since = GHL_SINCE }) {
  return bookings
    .filter(b => !b.is_test && !String(b.booking_status || '').toLowerCase().startsWith('cancel') && String(b.event_date || '') < '2099-01-01' && String(b.created_at || '').slice(0, 10) >= since)
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .map(b => {
      const c = contacts.get(b.customer_id) || {}
      return {
        booking_id: b.id,
        client_id: b.customer_id || null,
        client_name: names.get(b.customer_id) || null,
        event_date: String(b.event_date || '').slice(0, 10) || null,
        detail: [`Booked ${denverWhen(b.created_at)}`, c.email, c.phone].filter(Boolean).join(' · '),
        action: 'ghl'
      }
    })
}

const lower = v => String(v || '').toLowerCase()
const dateOnly = v => (v ? String(v).slice(0, 10) : '')

/** The stored key for one cleared item. */
export const dismissalKey = (cardKey, itemKey) => `${cardKey}::${itemKey}`

/** A row's item key: set explicitly by the card, else its booking, talent or flag. */
export function itemKeyOf(row) {
  return row?.item_key || row?.booking_id || row?.talent_id || row?.flag_id || null
}

export function isClearable(cardKey) {
  return !NOT_CLEARABLE.has(cardKey)
}

/** Admin clears anything; a department clears its own cards. */
export function canClear(roles, department) {
  const has = r => (roles instanceof Set ? roles.has(r) : Array.isArray(roles) && roles.includes(r))
  return has('admin') || (Boolean(department) && has(department))
}

/**
 * Remove cleared items from every card and mark which cards can be cleared.
 * @param {Record<string, object[]>} departments  department -> cards ({ key, rows, count, status })
 * @param {Set<string>|string[]} dismissed         dismissalKey values
 */
export function applyDismissals(departments, dismissed) {
  const gone = dismissed instanceof Set ? dismissed : new Set(dismissed || [])
  const out = {}
  for (const [dept, cards] of Object.entries(departments || {})) {
    out[dept] = (cards || []).map(c => {
      const clearable = c.status === 'live' && isClearable(c.key)
      if (!clearable || !Array.isArray(c.rows)) return { ...c, clearable }
      const rows = c.rows.map(r => ({ ...r, item_key: itemKeyOf(r) })).filter(r => !r.item_key || !gone.has(dismissalKey(c.key, r.item_key)))
      return { ...c, clearable, rows, count: rows.length }
    })
  }
  return out
}

/** The Editing board shows the same items as Editing's cards, so a cleared item leaves the board too. */
export function applyDismissalsToEditing(editing, dismissed) {
  if (!editing) return editing
  const gone = dismissed instanceof Set ? dismissed : new Set(dismissed || [])
  const queues = {}
  for (const [k, rows] of Object.entries(editing.queues || {})) queues[k] = rows.filter(r => !gone.has(dismissalKey(k, r.key)))
  const counts = Object.fromEntries(Object.entries(queues).map(([k, rows]) => [k, rows.length]))
  const overdue = Object.values(queues).reduce((n, rows) => n + rows.filter(r => r.risk === 'overdue').length, 0)
  return { ...editing, queues, counts, overdue }
}

/** Cancelled within `hours` of being booked. */
export function cancelledWithin(booking, hours = REFUND_WINDOW_HOURS) {
  const made = Date.parse(booking?.created_at || '')
  const gone = Date.parse(booking?.cancelled_at || '')
  if (!Number.isFinite(made) || !Number.isFinite(gone)) return false
  return gone >= made && gone - made <= hours * 3600 * 1000
}

const isCancelled = b => lower(b?.booking_status).startsWith('cancel')
const isCashRefund = x => lower(x?.status) === 'succeeded' && (!x.adjustment_type || ['cash_refund', 'cash_and_price_reduction'].includes(x.adjustment_type))
const money = cents => `$${Math.round(Number(cents || 0) / 100).toLocaleString('en-US')}`
const shortWhen = iso => {
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? d.toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
}

/**
 * @param {object} p
 * @param {object[]} p.bookings  rows with id, customer_id, created_at, cancelled_at, booking_status, cancellation_reason, event_date, is_test
 * @param {object[]} p.balances  booking_balances rows (booking_id, paid_cents)
 * @param {object[]} p.refunds   refunds rows for those bookings (booking_id, status, adjustment_type, amount_cents)
 * @param {Map}      p.names     customer id -> name
 * @returns {{ refund24h: object[], cancellations: object[] }}
 */
export function buildCancellationRows({ bookings = [], balances = [], refunds = [], names = new Map(), since = CANCELLATION_CARDS_SINCE }) {
  const paidBy = new Map(balances.map(r => [r.booking_id, Number(r.paid_cents || 0)]))
  const refundedBy = new Map()
  for (const x of refunds) if (isCashRefund(x)) refundedBy.set(x.booking_id, (refundedBy.get(x.booking_id) || 0) + Number(x.amount_cents || 0))
  const refund24h = []
  const cancellations = []
  const recent = bookings
    .filter(b => isCancelled(b) && !b.is_test && dateOnly(b.cancelled_at || b.updated_at) >= since)
    .sort((a, b) => String(b.cancelled_at || '').localeCompare(String(a.cancelled_at || '')))
  for (const b of recent) {
    const paid = paidBy.has(b.id) ? paidBy.get(b.id) : null
    const refunded = refundedBy.get(b.id) || 0
    const base = {
      booking_id: b.id,
      client_id: b.customer_id || null,
      client_name: names.get(b.customer_id) || null,
      event_date: dateOnly(b.event_date) || null,
      cancelled_at: b.cancelled_at || null,
      paid_cents: paid,
      refunded_cents: refunded
    }
    const why = String(b.cancellation_reason || '').trim()
    if (cancelledWithin(b) && paid !== 0) {
      if (refunded > 0) continue // refund recorded: the card clears itself
      refund24h.push({ ...base, detail: `Booked ${shortWhen(b.created_at)}, cancelled ${shortWhen(b.cancelled_at)}${paid ? ` · ${money(paid)} paid` : ''}${why ? ` · ${why}` : ''}`, action: 'refund' })
    } else {
      cancellations.push({ ...base, detail: `Cancelled ${shortWhen(b.cancelled_at || b.updated_at)}${paid ? ` · ${money(paid)} paid` : ''}${refunded ? ` · ${money(refunded)} refunded` : ''}${why ? ` · ${why}` : ''}` })
    }
  }
  return { refund24h, cancellations }
}
